import { zodResolver } from '@hookform/resolvers/zod'
import React, { useMemo, useState } from 'react'
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
  useFormContext,
  useWatch,
} from 'react-hook-form'
import { z } from 'zod'

import {
  AirplanemodeActive,
  Explore,
  FmdBad,
  HourglassBottomRounded,
  Save,
  TodayRounded,
  Visibility,
} from '@mui/icons-material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'

import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'

type CommonPolicy = {
  id?: number
  name: string
  description: string
  isImmediateJourney: boolean
  isDestinationSupplier: boolean
  isAirport: boolean
  isHourlyBooking: boolean
  status: 'Active' | 'Deleted' | string
}

type CancellationCharge = {
  id?: number
  status: string
  minutes: number
  chargeTypeName: string
  value: number
}

export type CancellationPolicy = CommonPolicy & {
  fullyRefundableUntil: string
  charges: CancellationCharge[]
}

type NoShowPolicy = CommonPolicy & {
  id?: number
  chargeTypeName: string
  value: number
}

type AmendRule = {
  id?: number
  status: string
  minutes: number
  changeTypeName: string
}

type AmendPolicy = CommonPolicy & {
  rules: AmendRule[]
}

type FieldDef =
  | { key: string; label: string; type: 'text' | 'textarea' | 'number' }
  | { key: string; label: string; type: 'boolean' }
  | { key: string; label: string; type: 'select'; options: string[] }

type DetailCol =
  | { key: string; label: string; type: 'text' | 'number' }
  | { key: string; label: string; type: 'select'; options: string[] }

type PolicyConfig<T> = {
  title: string
  policyType: 'cancellation' | 'no_show' | 'amend'
  topFields: FieldDef[] // fields shown at top of modal
  detailKey?: keyof T & string // e.g. "charges" | "rules" (or undefined for No-Show)
  detailTitle?: string // e.g. "Charges" or "Rules"
  detailHelper?: string
  detailCols?: DetailCol[]
  rowBadges?: (row: T) => React.ReactNode // additional chips in the table
  statusOptions?: string[] // status dropdown choices
  newRow: (nextId: number) => T // factory to create a new row
}

function RHFText({
  name,
  label,
  type = 'text',
  editPolicy,
}: {
  name: string
  label: string
  type?: 'text' | 'number'
  editPolicy: boolean
}) {
  const { register, formState } = useFormContext()
  const error = formState.errors as any
  return (
    <TextField
      fullWidth
      size="small"
      label={label}
      disabled={!editPolicy}
      type={type === 'number' ? 'number' : 'text'}
      {...register(name, { valueAsNumber: type === 'number' })}
      error={!!error?.[name]}
      required={!!error?.[name]}
      helperText={error?.[name]?.message as string}
    />
  )
}

import notifyPopup from 'components/Common/Notification/notifyPopup'
import { useLayoutEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'

function toTitleCase(str: string) {
  return str
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

export function AnimatedChip({
  label,
  color,
  icon,
  size,
}: {
  label: string
  size?: string
  color?:
    | 'primary'
    | 'secondary'
    | 'default'
    | 'error'
    | 'info'
    | 'success'
    | 'warning'
  icon: React.ReactElement
}) {
  const labelRef = useRef<HTMLSpanElement>(null)
  const [labelWidth, setLabelWidth] = useState(0)
  const [hovered, setHovered] = useState(false)

  useLayoutEffect(() => {
    if (labelRef.current) {
      setLabelWidth(labelRef.current.offsetWidth)
    }
  }, [label])

  // Icon width (Chip's icon area is about 32px)
  const iconWidth = 27
  // Total width: icon + label + padding
  const expandedWidth = iconWidth + labelWidth + 16

  return (
    <Chip
      size={size ? size : 'small'}
      color={color}
      icon={icon}
      label={
        <span
          ref={labelRef}
          style={{
            opacity: hovered ? 1 : 0,
            transition: 'opacity 0.2s 0.2s, padding-right 0.3s',
            paddingLeft: hovered ? 8 : 0,
            paddingRight: hovered ? 8 : 0,

            whiteSpace: 'nowrap',
            display: 'inline-block',
          }}
        >
          {label}
        </span>
      }
      sx={{
        overflow: 'hidden',
        justifyContent: 'flex-start',
        width: hovered ? expandedWidth : iconWidth,
        transition: 'width 0.3s cubic-bezier(.4,0,.2,1), background 0.2s',
        background: hovered ? '#e3f2fd' : undefined,
        '.MuiChip-label': {
          paddingRight: '0 !important',
          paddingLeft: '0 !important',
          margin: 0,
          width: hovered ? 'auto' : 0,
        },
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    />
  )
}

function RHFTextarea({
  name,
  label,
  editPolicy,
}: {
  name: string
  label: string
  editPolicy: boolean
}) {
  const { register } = useFormContext()
  return (
    <TextField
      fullWidth
      size="small"
      disabled={!editPolicy}
      label={label}
      multiline
      minRows={3}
      {...register(name)}
    />
  )
}

function RHFCheckbox({
  name,
  label,
  editPolicy,
}: {
  name: string
  label: string
  editPolicy: boolean
}) {
  const { control } = useFormContext()
  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <FormControlLabel
          control={
            <Checkbox
              checked={!!field.value}
              disabled={!editPolicy}
              onChange={(e) => field.onChange(e.target.checked)}
            />
          }
          label={label}
        />
      )}
    />
  )
}

function RHFSelect({
  name,
  label,
  options,
  editPolicy,
}: {
  name: string
  label: string
  options: string[]
  editPolicy: boolean
}) {
  const { control } = useFormContext()
  return (
    <FormControl fullWidth size="small">
      <InputLabel>{label}</InputLabel>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <Select
            label={label}
            {...field}
            disabled={!editPolicy}
            value={field.value ?? ''}
            onChange={(e) => field.onChange(e.target.value)}
          >
            {options.map((o) => (
              <MenuItem key={o} value={o}>
                {o === 'OnRoute' ? 'On Route' : o.replaceAll('_', ' ')}
              </MenuItem>
            ))}
          </Select>
        )}
      />
    </FormControl>
  )
}

function Field({
  basePath,
  def,
  editPolicy,
}: {
  basePath: string
  def: FieldDef
  editPolicy: boolean
}) {
  const name = `${basePath}.${def.key}`
  if (def.type === 'text' || def.type === 'number')
    return (
      <RHFText
        name={name}
        label={def.label}
        type={def.type === 'number' ? 'number' : 'text'}
        editPolicy={editPolicy}
      />
    )
  if (def.type === 'textarea')
    return <RHFTextarea name={name} label={def.label} editPolicy={editPolicy} />
  if (def.type === 'boolean')
    return <RHFCheckbox name={name} label={def.label} editPolicy={editPolicy} />
  if (def.type === 'select')
    return (
      <RHFSelect
        name={name}
        label={def.label}
        options={def.options}
        editPolicy={editPolicy}
      />
    )
  return null
}

function DetailCell({
  name,
  col,
  editPolicy,
  rowIndex,
  detailPath,
}: {
  name: string
  col: DetailCol
  editPolicy: boolean
  rowIndex?: number
  detailPath?: string
}) {
  const { control, register, watch } = useFormContext()

  // Watch the status field if this is a minutes field
  const statusFieldName =
    rowIndex !== undefined && detailPath
      ? `${detailPath}.${rowIndex}.status`
      : undefined
  const status = statusFieldName ? watch(statusFieldName) : undefined

  // Watch the chargeTypeName field if this is a value field
  const chargeTypeFieldName =
    rowIndex !== undefined && detailPath
      ? `${detailPath}.${rowIndex}.chargeTypeName`
      : undefined
  const chargeType = chargeTypeFieldName
    ? watch(chargeTypeFieldName)
    : undefined

  if (col.type === 'number' || col.type === 'text') {
    const isMinutesField = col.key === 'minutes'
    const isValueField = col.key === 'value'

    let startAdornment = undefined
    let endAdornment = undefined

    if (isMinutesField && status) {
      const displayStatus =
        status === 'OnRoute'
          ? 'on route'
          : status.toLowerCase().replaceAll('_', ' ')
      startAdornment = (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ whiteSpace: 'nowrap', paddingRight: '5px' }}
        >
          {status === 'Confirmed' ? 'within' : ' '}
        </Typography>
      )
      endAdornment = (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ whiteSpace: 'nowrap', paddingLeft: '5px' }}
        >
          {status === 'Confirmed'
            ? 'minutes of the pick up time'
            : `minutes after ${displayStatus}`}
        </Typography>
      )
    }

    if (isValueField && chargeType) {
      if (chargeType === 'Fixed') {
        startAdornment = (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ whiteSpace: 'nowrap', paddingRight: '5px' }}
          >
            £
          </Typography>
        )
      } else if (chargeType === 'Percentage') {
        endAdornment = (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ whiteSpace: 'nowrap', paddingLeft: '5px' }}
          >
            %
          </Typography>
        )
      }
    }

    return (
      <TextField
        size="small"
        variant="outlined"
        disabled={!editPolicy}
        type={col.type === 'number' ? 'number' : 'text'}
        {...register(name, { valueAsNumber: col.type === 'number' })}
        InputProps={
          startAdornment || endAdornment
            ? {
                startAdornment,
                endAdornment,
              }
            : undefined
        }
      />
    )
  }
  if (col.type === 'select') {
    return (
      <FormControl fullWidth size="small">
        <Controller
          name={name}
          control={control}
          render={({ field }) => (
            <Select
              {...field}
              value={field.value ?? ''}
              disabled={!editPolicy}
              onChange={(e) => field.onChange(e.target.value)}
            >
              {col.options.map((o) => (
                <MenuItem key={o} value={o}>
                  {o === 'OnRoute' ? 'On Route' : o.replaceAll('_', ' ')}
                </MenuItem>
              ))}
            </Select>
          )}
        />
      </FormControl>
    )
  }
  return null
}

function EditModal<T extends Record<string, any>>({
  index,
  name,
  config,
  open,
  onOpenChange,
  editPolicy,
}: {
  index: number | null
  name: string // field array path, e.g. "cancellationPolicies"
  config: PolicyConfig<T>
  open: boolean
  onOpenChange: (v: boolean) => void
  editPolicy: boolean
}) {
  const basePath = `${name}.${index}`
  const detailPath = config.detailKey
    ? `${basePath}.${config.detailKey}`
    : undefined
  const { control, watch } = useFormContext()
  const { fields, append, remove } = useFieldArray({
    name: (detailPath as any) ?? '_no_details_',
    control,
  })

  // Watch all journey type fields
  const isImmediateJourney = watch(`${basePath}.isImmediateJourney`)
  const isDestinationSupplier = watch(`${basePath}.isDestinationSupplier`)
  const isAirport = watch(`${basePath}.isAirport`)
  const isHourlyBooking = watch(`${basePath}.isHourlyBooking`)

  // Check if any journey type is selected
  const hasJourneyTypeSelected =
    isImmediateJourney || isDestinationSupplier || isAirport || isHourlyBooking

  return (
    <Dialog
      open={open}
      onClose={() => onOpenChange(false)}
      fullWidth
      maxWidth="lg"
    >
      <DialogTitle
        sx={(theme) => ({
          backgroundColor: theme.palette.brand.main,
          color: `${theme.palette.primary.contrastText} !important`,
        })}
      >
        {config.title}
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {/* Non-boolean fields */}
          {config.topFields
            .filter((f) => f.type !== 'boolean')
            .map((f) => {
              return (
                <Grid item xs={12} md={6} key={f.key}>
                  <Field basePath={basePath} def={f} editPolicy={editPolicy} />
                </Grid>
              )
            })}

          <Grid item xs={12} md={6}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                border: '1px solid #eee',
                borderRadius: 1,
                padding: 2,
                background: '#fafafa',
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Applicable Journeys
              </Typography>

              {config.topFields
                .filter((f) => f.type === 'boolean')
                .map((f) => (
                  <Field
                    basePath={basePath}
                    def={f}
                    key={f.key}
                    editPolicy={editPolicy}
                  />
                ))}
              {!hasJourneyTypeSelected && (
                <Typography
                  sx={{ mb: 1, padding: 0, margin: 0 }}
                  fontSize={'14px'}
                >
                  Note: If no journey type is set, the policy applies to
                  pre-booked journeys only.
                </Typography>
              )}
            </Box>
          </Grid>
        </Grid>

        {config.detailKey && config.detailCols && (
          <Box mt={3}>
            <Typography variant="subtitle1" fontWeight={600}>
              {config.detailTitle}
            </Typography>
            <Typography fontWeight={400}>{config.detailHelper}</Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow
                    sx={(theme) => ({
                      backgroundColor: theme.palette.brand.main,
                      '*': {
                        color: `${theme.palette.primary.contrastText} !important`,
                      },
                    })}
                  >
                    {config.detailCols.map((c) => (
                      <TableCell key={c.key}>{c.label}</TableCell>
                    ))}
                    <TableCell align="right" width={64} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {fields.map((field, i) => (
                    <TableRow key={field.id}>
                      {config.detailCols!.map((c) => (
                        <TableCell key={c.key}>
                          <DetailCell
                            name={`${detailPath}.${i}.${c.key}`}
                            col={c}
                            editPolicy={editPolicy}
                            rowIndex={i}
                            detailPath={detailPath}
                          />
                        </TableCell>
                      ))}
                      <TableCell align="right">
                        {editPolicy && (
                          <IconButton size="small" onClick={() => remove(i)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {editPolicy && (
              <Stack
                direction="row-reverse"
                alignItems="center"
                justifyContent="space-between"
                mb={1}
              >
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => append({})}
                >
                  Add {config.detailTitle}
                </Button>
              </Stack>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onOpenChange(false)}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}

function PolicyTableControl<T extends Record<string, any>>({
  name,
  config,
  editPolicy = true,
}: {
  name: string
  config: PolicyConfig<T>
  editPolicy: boolean
}) {
  const { control } = useFormContext()
  const { fields, append } = useFieldArray({ name, control })
  const rows = useWatch({ name }) as T[]
  const [editIndex, setEditIndex] = useState<number | null>(null)
  const methods = useFormContext()

  const nextId = useMemo(() => {
    const max = (rows ?? []).reduce((m, r: any) => (r?.id > m ? r.id : m), 0)
    return max + 1
  }, [rows])

  const handleAdd = () => {
    append(config.newRow(nextId))
    setEditIndex(fields.length)
  }

  return (
    <Card elevation={1}>
      <CardHeader
        title={
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="h6">{config.title}</Typography>
          </Stack>
        }
      />
      <CardContent>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow
                sx={(theme) => ({
                  backgroundColor: theme.palette.brand.main,
                  '*': {
                    color: `${theme.palette.primary.contrastText} !important`,
                  },
                })}
              >
                <TableCell width={50} sx={{ textAlign: 'end' }}>
                  ID
                </TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Applicable Journeys</TableCell>
                <TableCell>{config.detailTitle}</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right" width={96} />
              </TableRow>
            </TableHead>
            <TableBody>
              {fields.map((f, i) => {
                const row = rows?.[i] as any
                if (row?.status === 'Deleted') return null

                return (
                  <TableRow key={f.id} hover>
                    <TableCell sx={{ textAlign: 'center' }}>
                      {row?.id < 1 ? (
                        <>
                          <AnimatedChip
                            color="default"
                            label="Draft"
                            icon={<Save />}
                          />
                        </>
                      ) : (
                        row?.id
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight={600}>{row?.name}</Typography>
                    </TableCell>

                    <TableCell sx={{ minWidth: 160, width: 350 }}>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap">
                        {row?.isImmediateJourney && (
                          <AnimatedChip label="Immediate" icon={<FmdBad />} />
                        )}
                        {row?.isDestinationSupplier && (
                          <AnimatedChip
                            label="Destination Supplier"
                            icon={<Explore />}
                          />
                        )}
                        {row?.isAirport && (
                          <AnimatedChip
                            label="Airport"
                            icon={<AirplanemodeActive />}
                          />
                        )}
                        {row?.isHourlyBooking && (
                          <AnimatedChip
                            label="Hourly"
                            icon={<HourglassBottomRounded />}
                          />
                        )}
                        {!row?.isDestinationSupplier &&
                          !row?.isImmediateJourney &&
                          !row?.isAirport &&
                          !row?.isHourlyBooking && (
                            <AnimatedChip
                              label="Pre-book"
                              icon={<TodayRounded />}
                            />
                          )}
                      </Stack>
                    </TableCell>

                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap">
                        {config.rowBadges?.(row)}
                      </Stack>
                    </TableCell>

                    <TableCell>
                      <Chip
                        color={row?.status === 'Active' ? 'primary' : 'default'}
                        size="small"
                        label={
                          row?.status === 'Deleted' ? 'Inactive' : row?.status
                        }
                      />
                    </TableCell>
                    {editPolicy ? (
                      <TableCell align="right" sx={{ display: 'flex' }}>
                        <IconButton
                          onClick={() => {
                            const updatedRows = [...rows]
                            updatedRows[i] = {
                              ...updatedRows[i],
                              status: 'Deleted',
                            }
                            methods.setValue(name, updatedRows)
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                        <IconButton onClick={() => setEditIndex(i)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    ) : (
                      <TableCell align="right" sx={{ display: 'flex' }}>
                        <IconButton onClick={() => setEditIndex(i)}>
                          <Visibility fontSize="small" />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                )
              })}
              {!fields.some((f, i) => rows?.[i]?.status === 'Active') && (
                <TableRow hover>
                  <TableCell colSpan={6} align="center">
                    <span style={{ fontWeight: 'bold' }}>
                      {toTitleCase(config.policyType.replaceAll('_', ' '))}
                    </span>{' '}
                    policies not set
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Stack
          paddingTop={1}
          direction="row-reverse"
          width="100% !important"
          alignItems="center"
          display="flex"
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              variant="outlined"
              size="small"
              label={`${
                fields.filter((f, i) => rows?.[i]?.status === 'Active')
                  .length ?? 0
              } policies`}
            />
            {editPolicy && (
              <Tooltip
                title={`Add ${config.title.replace(/s$/, '').toLowerCase()}`}
              >
                <Button
                  startIcon={<AddIcon />}
                  variant="contained"
                  size="small"
                  onClick={handleAdd}
                >
                  Add {config.policyType.replaceAll('_', ' ')} Policy
                </Button>
              </Tooltip>
            )}
          </Stack>
        </Stack>
      </CardContent>

      {editIndex !== null && (
        <EditModal<T>
          index={editIndex}
          name={name}
          config={config}
          open={editIndex !== null}
          onOpenChange={(v) => setEditIndex(v ? editIndex : null)}
          editPolicy={editPolicy}
        />
      )}
    </Card>
  )
}

const commonPolicySchema = z.object({
  id: z.number().optional(),
  name: z.string(),
  description: z.string().optional().default(''),
  isImmediateJourney: z.boolean().default(false),
  isDestinationSupplier: z.boolean().default(false),
  isAirport: z.boolean().default(false),
  isHourlyBooking: z.boolean().default(false),
  status: z.string().default('Active'),
})

const cancellationSchema = commonPolicySchema.extend({
  fullyRefundableUntil: z.string().default(''),
  charges: z
    .array(
      z.object({
        status: z.string(),
        minutes: z.coerce.number().int().nonnegative(),
        chargeTypeName: z.string(),
        value: z.coerce.number(),
      }),
    )
    .default([]),
})

const noShowSchema = commonPolicySchema.extend({
  chargeTypeName: z.string().default('Fixed'),
  value: z.coerce.number().default(0),
})

const amendSchema = commonPolicySchema.extend({
  rules: z
    .array(
      z.object({
        status: z.string(),
        minutes: z.coerce.number().int().nonnegative(),
        changeTypeName: z.string(),
      }),
    )
    .default([]),
})

const FormSchema = z.object({
  cancellationPolicies: z.array(cancellationSchema),
  noShowPolicies: z.array(noShowSchema),
  amendPolicies: z.array(amendSchema),
})

const cancellationConfig: PolicyConfig<CancellationPolicy> = {
  title: 'Cancellation Policies',
  policyType: 'cancellation',
  topFields: [
    { key: 'name', label: 'Name', type: 'text' },
    { key: 'description', label: 'Description', type: 'textarea' },
    {
      key: 'fullyRefundableUntil',
      label: 'Fully Refundable Until',
      type: 'textarea',
    },
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      options: ['Active', 'Deleted'],
    },
    { key: 'isImmediateJourney', label: 'Immediate Journey', type: 'boolean' },
    {
      key: 'isDestinationSupplier',
      label: 'Destination Supplier',
      type: 'boolean',
    },
    { key: 'isAirport', label: 'Airport', type: 'boolean' },
    { key: 'isHourlyBooking', label: 'Hourly Booking', type: 'boolean' },
  ],
  detailKey: 'charges',
  detailTitle: 'Charges',
  detailCols: [
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      options: ['OnRoute', 'Arrived', 'Confirmed', 'Driver_Allocated'],
    },
    { key: 'minutes', label: 'Timeframe', type: 'number' },
    {
      key: 'chargeTypeName',
      label: 'Charge Type',
      type: 'select',
      options: ['Fixed', 'Percentage'],
    },
    { key: 'value', label: 'Value', type: 'number' },
  ],
  rowBadges: (row) => (
    <Stack direction="row" spacing={0.5}>
      {Array.isArray(row?.charges) && row.charges.length ? (
        <Chip
          size="small"
          variant="outlined"
          label={`${row.charges.length} charges`}
        />
      ) : (
        <Chip size="small" color="error" label="No charges" />
      )}
    </Stack>
  ),
  newRow: (nextId) => ({
    id: -nextId,
    name: `New Cancellation #${nextId}`,
    description: '',
    fullyRefundableUntil: '',
    isImmediateJourney: false,
    isDestinationSupplier: false,
    isAirport: false,
    isHourlyBooking: false,
    status: 'Active',
    charges: [],
  }),
}

const noShowConfig: PolicyConfig<NoShowPolicy> = {
  title: 'No-Show Policies',
  policyType: 'no_show',
  topFields: [
    { key: 'name', label: 'Name', type: 'text' },
    { key: 'description', label: 'Description', type: 'textarea' },
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      options: ['Active', 'Deleted'],
    },
    {
      key: 'chargeTypeName',
      label: 'Charge Type',
      type: 'select',
      options: ['Fixed', 'Percentage'],
    },
    { key: 'value', label: 'Value', type: 'number' },
    { key: 'isImmediateJourney', label: 'Immediate Journey', type: 'boolean' },
    {
      key: 'isDestinationSupplier',
      label: 'Destination Supplier',
      type: 'boolean',
    },
    { key: 'isAirport', label: 'Airport', type: 'boolean' },
    { key: 'isHourlyBooking', label: 'Hourly Booking', type: 'boolean' },
  ],
  newRow: (nextId) => ({
    id: -nextId,
    name: `New No-Show #${nextId}`,
    description: '',
    isImmediateJourney: false,
    isDestinationSupplier: false,
    isAirport: false,
    isHourlyBooking: false,
    status: 'Active',
    chargeTypeName: 'Fixed',
    value: 0,
  }),
}

const amendConfig: PolicyConfig<AmendPolicy> = {
  title: 'Amendment Policies',
  policyType: 'amend',
  topFields: [
    { key: 'name', label: 'Name', type: 'text' },
    { key: 'description', label: 'Description', type: 'textarea' },
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      options: ['Active', 'Inactive'],
    },
    { key: 'isImmediateJourney', label: 'Immediate Journey', type: 'boolean' },
    {
      key: 'isDestinationSupplier',
      label: 'Destination Supplier',
      type: 'boolean',
    },
    { key: 'isHourlyBooking', label: 'Hourly Booking', type: 'boolean' },
  ],
  detailKey: 'rules',
  detailTitle: 'Rules',
  detailHelper:
    'Enter the rules that block booking changes under certain conditions.',
  detailCols: [
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      options: ['OnRoute', 'Arrived', 'Confirmed', 'Driver_Allocated'],
    },
    { key: 'minutes', label: 'Timeframe', type: 'number' },
    {
      key: 'changeTypeName',
      label: 'Change Type',
      type: 'select',
      options: ['Requotable', 'Non_Requotable'],
    },
  ],
  rowBadges: (row) => (
    <Stack direction="row" spacing={0.5}>
      {Array.isArray(row?.rules) && row.rules.length ? (
        <Chip
          size="small"
          variant="outlined"
          label={`${row.rules.length} rules`}
        />
      ) : (
        <Chip size="small" color="error" label="No rules" />
      )}
    </Stack>
  ),
  newRow: (nextId) => {
    return {
      id: -nextId,
      name: `New Amendment #${nextId}`,
      description: '',
      isImmediateJourney: false,
      isDestinationSupplier: false,
      isAirport: false,
      isHourlyBooking: false,
      status: 'Active',
      rules: [],
    }
  },
}

type FormValues = z.infer<typeof FormSchema>

export default function Policies({ ...props }) {
  const { cancellationPolicies, noShowPolicies, amendPolicies } = {
    ...props.policies,
  }

  const dispatch = useDispatch()
  const methods = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      cancellationPolicies: cancellationPolicies,
      noShowPolicies: noShowPolicies,
      amendPolicies: amendPolicies,
    },
    mode: 'onChange',
  })
  const onSubmit = async (values: FormValues) => {
    try {
      const filteredValues = {
        ...values,
        cancellationPolicies: values.cancellationPolicies
          .filter((p) => p.status === 'Active')
          .map(({ id, ...policy }) =>
            id && id > 0 ? { id, ...policy } : policy,
          ),
        noShowPolicies: values.noShowPolicies
          .filter((p) => p.status === 'Active')
          .map(({ id, ...policy }) =>
            id && id > 0 ? { id, ...policy } : policy,
          ),
        amendPolicies: values.amendPolicies
          .filter((p) => p.status === 'Active')
          .map(({ id, ...policy }) =>
            id && id > 0 ? { id, ...policy } : policy,
          ),
      }
      const action = props.updateFn({
        ...props.updateSchema,
        data: filteredValues,
      })
      const result = await dispatch(action)
      if ((result as any)?.error) {
        notifyPopup({
          type: 'danger',
          title: 'Unable to save policy changes',
          message: 'Please try again.',
        })
        return
      }

      notifyPopup({
        type: 'success',
        title: 'Policy changes saved',
        message: 'Your policy updates have been submitted.',
      })
    } catch (err) {
      notifyPopup({
        type: 'danger',
        title: 'Unable to save policy changes',
        message: 'Please try again.',
      })
      return Promise.reject(err)
    }
  }

  return (
    <FormProvider {...methods}>
      <Box p={3} maxWidth={1280} mx="auto">
        <Stack spacing={3}>
          <PolicyTableControl<CancellationPolicy>
            name="cancellationPolicies"
            config={cancellationConfig}
            editPolicy={props.editPolicy}
          />

          <PolicyTableControl<NoShowPolicy>
            name="noShowPolicies"
            config={noShowConfig}
            editPolicy={props.editPolicy}
          />
          <PolicyTableControl<AmendPolicy>
            name="amendPolicies"
            config={amendConfig}
            editPolicy={props.editPolicy}
          />

          <Stack direction="row" justifyContent="flex-end" spacing={2}>
            {methods.formState.isDirty && (
              <Button
                variant="contained"
                onClick={async () => {
                  try {
                    methods.reset()
                  } catch (err) {
                    console.log(err)
                  }
                }}
                sx={{ minWidth: 140 }}
              >
                Reset
              </Button>
            )}
            <Button
              variant="contained"
              disabled={!props.editPolicy}
              onClick={methods.handleSubmit(onSubmit, () => {
                notifyPopup({
                  type: 'danger',
                  title: 'Unable to save policy changes',
                  message: 'Please fix validation errors and try again.',
                })
              })}
              sx={{ minWidth: 140 }}
            >
              Save Policy Changes
            </Button>
          </Stack>
        </Stack>
      </Box>
    </FormProvider>
  )
}
